import { useState } from 'react';
import type { HITLRendererProps } from '../HITLRenderer';
import { hitlStyles, cn } from './styles';

export const SingleChoiceRenderer: React.FC<HITLRendererProps> = ({
  response,
  onSubmit,
  onSpecialAction,
  isSubmitting: externalSubmitting,
  compact,
}) => {
  const [selected, setSelected] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [isLocalSubmitting, setIsLocalSubmitting] = useState(false);
  
  const isSubmitting = externalSubmitting || isLocalSubmitting;

  const handleSelect = async (optionId: string) => {
    const option = response.options?.find(o => o.id === optionId);
    
    // Check for special actions (OAuth, etc.)
    if (option?.metadata?.action && onSpecialAction) {
      try {
        await onSpecialAction(option.metadata.action as string, option.metadata);
        // After special action, submit the answer
        onSubmit(optionId);
      } catch (error) {
        console.error('Special action failed:', error);
      }
      return;
    }

    setSelected(optionId);
  };

  const handleSubmit = async () => {
    if (selected) {
      setIsLocalSubmitting(true);
      try {
        await onSubmit(selected);
      } finally {
        // Keep spinning - the component will be replaced when state updates
        // setIsLocalSubmitting(false);
      }
    }
  };

  const toggleExpand = (optionId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedId(expandedId === optionId ? null : optionId);
  };

  // Check if any option has description or rich metadata (for expand feature)
  const hasExpandableContent = response.options?.some(
    o => o.description || (o.metadata && Object.keys(o.metadata).length > 1)
  );

  return (
    <div className={compact ? hitlStyles.containerCompact : hitlStyles.container}>
      {response.message && (
        <p className={hitlStyles.message}>{response.message}</p>
      )}
      
      {response.context && (
        <p className={hitlStyles.context}>{response.context}</p>
      )}

      <div className="space-y-2 mb-3">
        {response.options?.map((option) => {
          const isExpanded = expandedId === option.id;
          const hasDetails = option.description || (option.metadata && Object.keys(option.metadata).length > 1);
          
          return (
            <div key={option.id} className="group">
              <button
                onClick={() => handleSelect(option.id)}
                disabled={isSubmitting}
                className={cn(
                  hitlStyles.optionButton,
                  'flex-col items-start text-left',
                  option.isPrimary 
                    ? hitlStyles.optionButtonPrimary
                    : selected === option.id
                      ? hitlStyles.optionButtonSelected
                      : hitlStyles.optionButtonDefault
                )}
              >
                <div className="flex items-center w-full">
                  {!option.isPrimary && (
                    <span className="inline-flex items-center justify-center w-4 h-4 mr-2 border border-domino-cardBorder rounded-full flex-shrink-0">
                      {selected === option.id && (
                        <span className="block w-2 h-2 bg-domino-aubergine rounded-full" />
                      )}
                    </span>
                  )}
                  <span className="flex-1 font-medium">{option.label || 'Unknown option'}</span>
                  {hasDetails && hasExpandableContent && (
                    <button
                      onClick={(e) => toggleExpand(option.id, e)}
                      className="ml-2 p-1 text-domino-inkSoft hover:text-domino-ink transition-colors"
                      title={isExpanded ? 'Hide details' : 'Show details'}
                    >
                      <svg 
                        className={cn('w-4 h-4 transition-transform', isExpanded && 'rotate-180')}
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                  )}
                </div>
                
                {/* Description preview (always shown if available) */}
                {option.description && !isExpanded && (
                  <p className="text-xs text-domino-inkSoft mt-1 ml-6 truncate w-full">
                    {option.description}
                  </p>
                )}
              </button>
              
              {/* Expanded details */}
              {isExpanded && hasDetails && (
                <div className="ml-6 mt-1 p-3 bg-domino-muted rounded-lg border border-domino-cardBorder text-xs space-y-1">
                  {option.description && (
                    <p className="text-domino-inkSoft mb-2">{option.description}</p>
                  )}
                  {option.metadata && Object.entries(option.metadata)
                    .filter(([key, value]) => value && key !== 'action' && key !== 'provider')
                    .map(([key, value]) => (
                      <div key={key} className="flex">
                        <span className="text-domino-inkSoft capitalize w-24 flex-shrink-0">{key}:</span>
                        <span className="text-domino-ink">{String(value)}</span>
                      </div>
                    ))
                  }
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Show submit button only if no option has special action */}
      {!response.options?.some(o => o.metadata?.action) && (
        <button
          onClick={handleSubmit}
          disabled={!selected || isSubmitting}
          className={cn(hitlStyles.actionButton, hitlStyles.actionButtonPrimary, 'w-full')}
        >
          {isSubmitting ? (
            <span className="flex items-center justify-center gap-2">
              <span className={hitlStyles.spinner} />
              Processing...
            </span>
          ) : (
            'Continue'
          )}
        </button>
      )}
    </div>
  );
};
